/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import axios from 'axios';
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";

/**
 * HOC for the url groups form
 */
class UrlGroupsForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            urlGroupId: null,
            urlGroupName: '',
            courseURL:'',
            enrollURL: '',
            ssoURL: '',
            userURL: '',
            outcomesURL: '',
            isDisplayLoader: true,
            errors: {},

            token: '',
            fields: [
                {
                    inputType: 'text',
                    labelName: 'Name *',
                    inputName: 'urlGroupName',
                    placeholder: 'Please enter URL Group name here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'textUrl',
                    labelName: 'Course Url *',
                    inputName: 'courseURL',
                    placeholder: 'Please enter Course URL here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'textUrl',
                    labelName: 'Enroll Url *',
                    inputName: 'enrollURL',
                    placeholder: 'Please enter Enroll URL here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'textUrl',
                    labelName: 'CourseRole Url *',
                    inputName: 'ssoURL',
                    placeholder: 'Please enter CourseRole URL here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'textUrl',
                    labelName: 'User Url *',
                    inputName: 'userURL',
                    placeholder: 'Please enter User URL here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'textUrl',
                    labelName: 'Outcomes Url',
                    inputName: 'outcomesURL',
                    placeholder: 'Please enter Outcomes URL here',
                    required: false,
                    disabled: false
                }
            ]
        };

        // get the parameter from URL
        const urlGroupId = this.props.match.params.id;

        // set icons to the header
        if (urlGroupId === 'create') {
            EventEmitter.emit('setPageHeaderData', {
                page: 'url-groups',
                pageName: "URL Groups",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/url-groups",
                optionalIcons: ['backIcon','clearFormIcon']
            });
        } else {
            EventEmitter.emit('setPageHeaderData', {
                page: 'url-groups',
                pageName: "URL Groups",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/url-groups",
                optionalIcons: ['backIcon','clearFormIcon','deleteIcon']
            });
        }

        // set the event emitters
        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('deleteFormData', this.deletePartnerUrl);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }

    // set a token to unsubscribe the async call
    CancelToken = axios.CancelToken;
    source = this.CancelToken.source();

    componentDidMount = async () => {
        // get parameter from URL
        const urlGroupId = this.props.match.params.id;

        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);

        // fill the form if not a create url group
        if (urlGroupId !== 'create') {

            const url = '/url-group?search=%7BurlGroupId:' + urlGroupId + '%7D';

            await API.get(url, {headers: {Authorization: token}, cancelToken: this.source.token})
                .then(res => {
                    const data = res.data[0];
                    oldState.urlGroupId = (data.urlGroupId !== undefined) ? data.urlGroupId : null;
                    oldState.urlGroupName = (data.urlGroupName !== undefined) ? data.urlGroupName : '';
                    oldState.courseURL = (data.courseURL !== undefined) ? data.courseURL : '';
                    oldState.enrollURL = (data.enrollURL !== undefined) ? data.enrollURL : '';
                    oldState.ssoURL = (data.ssoURL !== undefined) ? data.ssoURL : '';
                    oldState.userURL = (data.userURL !== undefined) ? data.userURL : '';
                    oldState.outcomesURL = (data.outcomesURL !== undefined) ? data.outcomesURL : '';
                    oldState.token = token;
                    oldState.isDisplayLoader = false;

                    this.setState(oldState);
                })
                .catch(er => {
                    oldState.isDisplayLoader = false;
                    this.setState(oldState);
                    this.displayErrorsForAPICalls(er);
                });
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        // remove all event emitters
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('deleteFormData', this.deletePartnerUrl);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
        this.source.cancel("cancel url group async calls");
    };

    /**
     * set the state
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    /**
     * handle the form submission
     */
    handleSubmit = () => {
        // data to be sent
        let data = null;
        // check its a update or create
        if (this.state.urlGroupId !== null) {
            data = {
                urlGroupId: this.state.urlGroupId,
                urlGroupName: this.state.urlGroupName,
                courseURL: this.state.courseURL,
                enrollURL: this.state.enrollURL,
                ssoURL: this.state.ssoURL,
                userURL: this.state.userURL,
                outcomesURL: this.state.outcomesURL
            };
        } else {
            data = {
                urlGroupName: this.state.urlGroupName,
                courseURL: this.state.courseURL,
                enrollURL: this.state.enrollURL,
                ssoURL: this.state.ssoURL,
                userURL: this.state.userURL,
                outcomesURL: this.state.outcomesURL
            };
        }

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            // check update or create
            if (this.state.urlGroupId !== null) {
                this.updateUrlGroups(data);
            } else {
                this.createUrlGroup(data);
            }
        }
    };

    /**
     * validate the user inputs
     * @param key - input name
     * @param value - input value
     */
    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'urlGroupName':
                if (value === '')
                    error[key] = "URL Group Name is Required!";
                else if (value === ' ')
                    error[key] = "URL Group Name can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "URL Group Name can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            case 'courseURL':
                if (value === '')
                    error[key] = "Course Url is Required!";
                else if (value === ' ')
                    error[key] = "Course Url can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 128))
                    error[key] = "Course Url can't have more than 128 characters!";
                else if ((value.indexOf("https://") === 0) || (value.indexOf("http://") === 0) || (value.indexOf("/") === 0))
                    error[key] = "";
                else
                    error[key] = "Course Url must begin with with http://, https:// or / ";
                break;
            case 'enrollURL':
                if (value === '')
                    error[key] = "Enroll Url is Required!";
                else if (value === ' ')
                    error[key] = "Enroll Url can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 128))
                    error[key] = "Enroll Url can't have more than 128 characters!";
                else if ((value.indexOf("https://") === 0) || (value.indexOf("http://") === 0) || (value.indexOf("/") === 0))
                    error[key] = "";
                else
                    error[key] = "Enroll Url must begin with with http://, https:// or / ";
                break;
            case 'ssoURL':
                if (value === '')
                    error[key] = "CourseRole Url is Required!";
                else if (value === ' ')
                    error[key] = "CourseRole Url can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 128))
                    error[key] = "CourseRole Url can't have more than 128 characters!";
                else if ((value.indexOf("https://") === 0) || (value.indexOf("http://") === 0) || (value.indexOf("/") === 0))
                    error[key] = "";
                else
                    error[key] = "CourseRole Url must begin with with http://, https:// or / ";
                break;
            case 'userURL':
                if (value === '')
                    error[key] = "User Url is Required!";
                else if (value === ' ')
                    error[key] = "User Url can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 128))
                    error[key] = "User Url can't have more than 128 characters!";
                else if ((value.indexOf("https://") === 0) || (value.indexOf("http://") === 0) || (value.indexOf("/") === 0))
                    error[key] = "";
                else
                    error[key] = "User Url must begin with with http://, https:// or / ";
                break;
            case 'outcomesURL':
                if (value !== null && value !== '') {
                    if ((value.indexOf("https://") === 0) || (value.indexOf("http://") === 0) || (value.indexOf("/") === 0))
                        error[key] = "";
                    else
                        error[key] = "User Url must begin with with http://, https:// or / ";
                } else {
                    error[key] = "";
                }
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    /**
     * create url groups
     * @param data - the data to create a new url group
     */
    createUrlGroup = async (data) => {
        await API.post('/url-group',
            {
                "urlGroupName": data.urlGroupName,
                "courseURL": data.courseURL,
                "enrollURL": data.enrollURL,
                "ssoURL": data.ssoURL,
                "userURL": data.userURL,
                "outcomesURL": data.outcomesURL
            }, {headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(res => {
            this.setAlert("Success", "Success", "Successfully Created the URL Group");
            this.props.history.push('/home/url-groups');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * update url-groups
     * @param data - updated url-groups details
     */
    updateUrlGroups = async (data) => {
        await API.put('/url-group',
            {
                "urlGroupId": data.urlGroupId,
                "urlGroupName": data.urlGroupName,
                "courseURL": data.courseURL,
                "enrollURL": data.enrollURL,
                "ssoURL": data.ssoURL,
                "userURL": data.userURL,
                "outcomesURL": data.outcomesURL
            },{headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(response => {
            this.setAlert("Success", "Success", "Successfully updated the URL Group");
            this.props.history.push('/home/url-groups');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * display delete warning message
     */
    displayDeleteWarningMessage = () => {
        if (this.state.courseURL !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'url-groups',
                name: this.state.urlGroupName
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the URL Group",
                alertTitle: "Error"
            });
        }

    };

    /**
     * delete a url group
     * @param data
     */
    deletePartnerUrl = async (data) => {
        if (data && (this.props.match.path === "/home/url-groups/:id")) {
            await API.delete('/url-group?urlGroupId=' + this.state.urlGroupId, {headers: {Authorization: this.state.token}, cancelToken: this.source.token})
                .then(res => {
                    this.setAlert("Success", "Success", "Successfully Deleted the URL group");
                    this.props.history.push('/home/url-groups');
                }).catch(er => {
                    this.displayErrorsForAPICalls(er);
                });
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "URL group is already exist!");
        } else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    /**
     * fire an alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    /**
     * clear the form
     */
    clearForm = () => {
        const newState = Object.assign({}, this.state);

        newState.urlGroupName = "";
        newState.courseURL = "";
        newState.enrollURL = "";
        newState.ssoURL = "";
        newState.userURL = "";
        newState.outcomesURL = "";
        newState.errors = {};

        this.setState(newState);
    };

    /**
     * navigate to previous page
     */
    navigateToPreviousPage = () => {
        this.props.history.push('/home/url-groups');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Save" onClick={this.handleSubmit}>
                        <i className="fa fa-save fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default UrlGroupsForm;
